/**
 * Admin: gestione metodi di pagamento (CRUD).
 * Sotto Impostazioni.
 */
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, CreditCard, Save, X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: '', description: '', deposit_percent: 50, balance_days: 0, terms: '', is_active: true, sort_order: 0 };

const AdminPaymentMethods = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(emptyForm);

  const auth = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/payment-methods`, { headers: auth() });
      if (res.ok) setMethods((await res.json()).data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminToken) load(); /* eslint-disable-next-line */ }, [adminToken]);

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({ ...emptyForm, ...m });
  };

  const startNew = () => { setEditing('new'); setForm(emptyForm); };
  const cancel = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name?.trim()) { toast({ title: 'Nome obbligatorio', variant: 'destructive' }); return; }
    const isNew = editing === 'new';
    const url = isNew ? `${API}/api/admin/payment-methods` : `${API}/api/admin/payment-methods/${editing}`;
    const method = isNew ? 'POST' : 'PUT';
    const res = await fetch(url, { method, headers: auth(), body: JSON.stringify(form) });
    if (res.ok) {
      toast({ title: isNew ? 'Metodo creato' : 'Metodo aggiornato' });
      cancel(); load();
    } else {
      const j = await res.json();
      toast({ title: 'Errore', description: j.detail, variant: 'destructive' });
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Eliminare "${m.name}"?`)) return;
    const res = await fetch(`${API}/api/admin/payment-methods/${m.id}`, { method: 'DELETE', headers: auth() });
    if (res.ok) { toast({ title: 'Eliminato' }); load(); }
  };

  const seed = async () => {
    const res = await fetch(`${API}/api/admin/payment-methods/seed-defaults`, { method: 'POST', headers: auth() });
    const j = await res.json();
    toast({ title: 'Seed default', description: j.message });
    load();
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Metodi di pagamento</h1>
            <p className="mt-1 text-gray-600">Configura i piani pagamento proposti agli agenti nei preventivi.</p>
          </div>
          <div className="flex gap-2">
            {methods.length === 0 && (
              <button onClick={seed} data-testid="btn-seed" className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-purple-300 text-purple-700 rounded-md text-sm hover:bg-purple-50">
                <Sparkles className="w-4 h-4" /> Carica default
              </button>
            )}
            <button onClick={startNew} data-testid="btn-new-method" className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm font-medium">
              <Plus className="w-4 h-4" /> Nuovo metodo
            </button>
          </div>
        </div>

        {editing && (
          <div className="bg-white rounded-2xl border border-purple-200 p-6" data-testid="edit-panel">
            <h2 className="text-lg font-semibold mb-4">{editing === 'new' ? 'Nuovo metodo' : 'Modifica'}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase text-gray-700">Nome *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="input-name"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase text-gray-700">Descrizione (mostrata a agente/PDF)</label>
                <input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-700">Acconto %</label>
                <input type="number" min="0" max="100" value={form.deposit_percent} onChange={(e) => setForm({ ...form, deposit_percent: Number(e.target.value) })}
                  data-testid="input-deposit"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-700">Saldo (giorni)</label>
                <input type="number" min="0" value={form.balance_days} onChange={(e) => setForm({ ...form, balance_days: Number(e.target.value) })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase text-gray-700">Termini (testo esteso in PDF)</label>
                <textarea rows={3} value={form.terms || ''} onChange={(e) => setForm({ ...form, terms: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-700">Ordine</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="flex items-center">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    data-testid="input-active" />
                  <span>Attivo</span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={cancel} className="inline-flex items-center gap-1 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md text-sm">
                <X className="w-4 h-4" /> Annulla
              </button>
              <button onClick={save} data-testid="btn-save"
                className="inline-flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm">
                <Save className="w-4 h-4" /> Salva
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-500">Caricamento…</div>
          ) : methods.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              <CreditCard className="w-8 h-8 mx-auto text-gray-300 mb-2" />
              Nessun metodo di pagamento configurato. Clicca "Carica default" per iniziare.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-purple-50/60 text-purple-700 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Descrizione</th>
                  <th className="text-right px-4 py-3">Acconto</th>
                  <th className="text-right px-4 py-3">Saldo (gg)</th>
                  <th className="text-center px-4 py-3">Attivo</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {methods.map((m) => (
                  <tr key={m.id} className="hover:bg-purple-50/40" data-testid={`method-row-${m.id}`}>
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3 text-gray-600">{m.description}</td>
                    <td className="px-4 py-3 text-right font-mono">{m.deposit_percent}%</td>
                    <td className="px-4 py-3 text-right font-mono">{m.balance_days}gg</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {m.is_active ? 'ATTIVO' : 'DISATT.'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(m)} className="p-1.5 rounded hover:bg-purple-100" data-testid={`edit-${m.id}`}>
                          <Pencil className="w-4 h-4 text-purple-600" />
                        </button>
                        <button onClick={() => remove(m)} className="p-1.5 rounded hover:bg-red-50" data-testid={`del-${m.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentMethods;
